/* iter466 — My AI Menus history page.
 *
 * Lists last 10 AI-generated weekly menus (Claude Sonnet 4.5 output) with:
 *   • Quick metadata (date, days, meals/day, conditions used)
 *   • Click → opens full MenuModal renderer
 *   • Re-generate button → fires same params for the new week
 *   • Delete → remove from history
 *
 * Premium driver: lets clinicians/users review what was eaten + tweak plan.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { toast } from "sonner";
import {
  ChefHat, Loader2, Trash2, RotateCw, Calendar, Tag, ArrowLeft, Sparkles, FlaskConical,
} from "lucide-react";
import MenuModalView from "@/components/foodlab/MenuModalView";

const T = {
  bg: {
    title: "Моите AI менюта",
    subtitle: "История на персонализираните седмични менюта",
    empty_title: "Все още няма генерирани менюта",
    empty_cta: "Отвори Nutrition Lab за първото",
    open: "Отвори",
    regenerate: "Пресъздай за тази седмица",
    regenerating: "Пресъздаване…",
    delete: "Изтрий",
    days_label: "дни",
    meals_label: "хранения/ден",
    conditions_label: "Състояния",
    back: "Назад към Health Intelligence",
  },
  en: {
    title: "My AI Menus",
    subtitle: "History of personalized weekly menus",
    empty_title: "No menus generated yet",
    empty_cta: "Open Nutrition Lab to create your first",
    open: "Open",
    regenerate: "Re-generate for this week",
    regenerating: "Re-generating…",
    delete: "Delete",
    days_label: "days",
    meals_label: "meals/day",
    conditions_label: "Conditions",
    back: "Back to Health Intelligence",
  },
  es: {
    title: "Mis menús de IA",
    subtitle: "Historial de menús semanales personalizados",
    empty_title: "Aún no se han generado menús",
    empty_cta: "Abre el Laboratorio de Nutrición para crear el primero",
    open: "Abrir",
    regenerate: "Regenerar para esta semana",
    regenerating: "Regenerando…",
    delete: "Eliminar",
    days_label: "días",
    meals_label: "comidas/día",
    conditions_label: "Condiciones",
    back: "Volver a Health Intelligence",
  },
};

export default function MyMenusPage() {
  const { API } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const lang = language === "en" ? "en" : "bg";
  const t = T[lang];

  const [items, setItems] = useState(null);
  const [activeMenu, setActiveMenu] = useState(null);
  const [activeMenuId, setActiveMenuId] = useState(null);  // iter481
  const [activeMenuProfile, setActiveMenuProfile] = useState(null);  // iter482
  const [regeneratingId, setRegeneratingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const txt = isDark ? "text-white" : "text-slate-900";
  const sub = isDark ? "text-slate-400" : "text-slate-500";
  const card = isDark ? "bg-[hsl(223,47%,9%)] border-[hsl(223,47%,16%)]" : "bg-white border-slate-200";

  const fetchMenus = () => {
    axios.get(`${API}/foodlab/menus?limit=10`)
      .then(({ data }) => setItems(data.items || []))
      .catch(() => setItems([]));
  };
  useEffect(() => { fetchMenus(); }, [API]); // eslint-disable-line react-hooks/exhaustive-deps

  // iter467 — Re-generate via async job pattern (avoids K8s 60s timeout).
  // Calls /menus/{id}/regenerate which internally just kicks off a new
  // generation; but the endpoint currently is sync. To stay safe under proxy
  // timeout we fall back to /generate-menu/start with the SAME params from the
  // original menu's saved request.
  const regenerate = async (menu) => {
    setRegeneratingId(menu.menu_id);
    try {
      const req = menu.request || {};
      const payload = {
        days:            req.days ?? 7,
        meals_per_day:   req.meals_per_day ?? 3,
        conditions:      req.conditions ?? [],
        diets:           req.diets ?? [],
        dil_target_g:    req.dil_target_g ?? 55,
        nis_target_pct:  req.nis_target_pct ?? 35,
        col3_target:     req.col3_target ?? 60,
        pinned_food_ids: req.pinned_food_ids ?? [],
        extra_constraints: req.extra_constraints,
      };
      const startRes = await axios.post(`${API}/foodlab/generate-menu/start`, payload, { timeout: 15000 });
      const jobId = startRes.data?.job_id;
      const start = Date.now();
      const MAX_MS = 240000;
      let finished = null;
      while (Date.now() - start < MAX_MS) {
        await new Promise((r) => setTimeout(r, 4000));
        const statusRes = await axios.get(`${API}/foodlab/generate-menu/status/${jobId}`, { timeout: 12000 });
        if (statusRes.data?.status === "done") { finished = statusRes.data; break; }
        if (statusRes.data?.status === "failed") throw new Error(statusRes.data?.error || "fail");
      }
      if (!finished) throw new Error("timeout");
      toast.success(lang === "bg" ? "Новото меню е готово" : "New menu ready");
      setActiveMenu(finished.menu);
      setActiveMenuId(finished.menu_id || null);  // iter481
      setActiveMenuProfile(finished.profile_applied || null);  // iter482
      fetchMenus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || (lang === "bg" ? "Грешка" : "Error"));
    } finally {
      setRegeneratingId(null);
    }
  };

  const deleteMenu = async (menuId) => {
    setDeletingId(menuId);
    try {
      await axios.delete(`${API}/foodlab/menus/${menuId}`);
      setItems((arr) => (arr || []).filter((m) => m.menu_id !== menuId));
      toast.success(lang === "bg" ? "Изтрито" : "Deleted");
    } catch {
      toast.error(lang === "bg" ? "Грешка" : "Error");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className={`min-h-screen ${isDark ? "bg-[hsl(223,47%,7%)]" : "bg-slate-50"}`} data-testid="my-menus-page">
      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Header */}
        <Link
          to="/dashboard/health-intelligence"
          className={`inline-flex items-center gap-1.5 text-xs mb-3 hover:underline ${sub}`}
          data-testid="my-menus-back"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          {t.back}
        </Link>

        <div className="flex items-start gap-3 mb-6">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-pink-500 to-violet-500 flex items-center justify-center shrink-0">
            <ChefHat className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className={`text-2xl sm:text-3xl font-bold ${txt}`}>{t.title}</h1>
            <p className={`text-sm mt-1 ${sub}`}>{t.subtitle}</p>
          </div>
        </div>

        {/* Loading */}
        {items === null && (
          <div className="flex items-center justify-center py-16">
            <Loader2 className={`w-8 h-8 animate-spin ${sub}`} />
          </div>
        )}

        {/* Empty state */}
        {items !== null && items.length === 0 && (
          <div className={`rounded-2xl border border-dashed py-12 text-center ${isDark ? "border-slate-700" : "border-slate-300"}`} data-testid="my-menus-empty">
            <Sparkles className={`w-12 h-12 mx-auto mb-3 ${sub}`} />
            <h3 className={`text-base font-semibold ${txt}`}>{t.empty_title}</h3>
            <Link
              to="/dashboard/nutrition-lab"
              className="inline-flex items-center gap-1.5 mt-4 px-4 py-2 rounded-lg bg-gradient-to-r from-pink-500 to-violet-500 text-white text-sm font-semibold hover:opacity-90"
              data-testid="my-menus-empty-cta"
            >
              <FlaskConical className="w-4 h-4" />
              {t.empty_cta}
            </Link>
          </div>
        )}

        {/* Menu cards */}
        {items !== null && items.length > 0 && (
          <div className="space-y-3">
            {items.map((m) => {
              const req = m.request || {};
              const conds = req.conditions || [];
              const created = new Date(m.created_at);
              const dateStr = created.toLocaleDateString(lang === "bg" ? "bg-BG" : "en-US", {
                day: "numeric", month: "short", year: "numeric",
              });
              const summary = m.menu?.weekly_summary_bg || "";
              return (
                <div
                  key={m.menu_id}
                  data-testid={`menu-card-${m.menu_id}`}
                  className={`rounded-2xl border p-4 ${card}`}
                >
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="flex-1 min-w-0">
                      <div className={`flex items-center gap-2 text-xs ${sub} mb-1`}>
                        <Calendar className="w-3.5 h-3.5" />
                        {dateStr}
                        <span className="opacity-50">·</span>
                        <span>{req.days || "?"} {t.days_label}</span>
                        <span className="opacity-50">·</span>
                        <span>{req.meals_per_day || "?"} {t.meals_label}</span>
                      </div>
                      {conds.length > 0 && (
                        <div className="flex items-center gap-1 flex-wrap mt-1.5">
                          <Tag className={`w-3 h-3 ${sub} shrink-0`} />
                          {conds.slice(0, 5).map((cid) => (
                            <span
                              key={cid}
                              className={`text-[10px] px-1.5 py-0.5 rounded-full ${isDark ? "bg-violet-500/15 text-violet-300" : "bg-violet-100 text-violet-700"}`}
                            >
                              {cid}
                            </span>
                          ))}
                          {conds.length > 5 && (
                            <span className={`text-[10px] ${sub}`}>+{conds.length - 5}</span>
                          )}
                        </div>
                      )}
                      {summary && (
                        <p className={`text-xs mt-2 leading-relaxed line-clamp-2 ${sub}`}>
                          {summary}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 mt-3">
                    <button
                      onClick={() => { setActiveMenu(m.menu); setActiveMenuId(m.menu_id); setActiveMenuProfile(m.profile_applied || null); }}
                      data-testid={`menu-open-${m.menu_id}`}
                      className="flex-1 py-2 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 bg-gradient-to-r from-pink-500 to-violet-500 text-white hover:opacity-90"
                    >
                      <ChefHat className="w-3.5 h-3.5" />
                      {t.open}
                    </button>
                    <button
                      onClick={() => regenerate(m)}
                      disabled={regeneratingId === m.menu_id}
                      data-testid={`menu-regen-${m.menu_id}`}
                      className={`flex-1 py-2 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 border transition-all disabled:opacity-50 ${
                        isDark ? "border-slate-600 text-slate-200 hover:bg-slate-800" : "border-slate-300 text-slate-700 hover:bg-slate-100"
                      }`}
                    >
                      {regeneratingId === m.menu_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCw className="w-3.5 h-3.5" />}
                      {regeneratingId === m.menu_id ? t.regenerating : t.regenerate}
                    </button>
                    <button
                      onClick={() => deleteMenu(m.menu_id)}
                      disabled={deletingId === m.menu_id}
                      data-testid={`menu-del-${m.menu_id}`}
                      aria-label={t.delete}
                      title={t.delete}
                      className={`p-2 rounded-lg ${isDark ? "text-slate-400 hover:bg-slate-800 hover:text-rose-400" : "text-slate-500 hover:bg-rose-50 hover:text-rose-500"} disabled:opacity-50`}
                    >
                      {deletingId === m.menu_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Menu viewer modal */}
      {activeMenu && (
        <MenuModalView
          menu={activeMenu}
          menuId={activeMenuId}
          profileApplied={activeMenuProfile}
          lang={lang}
          isDark={isDark}
          onClose={() => { setActiveMenu(null); setActiveMenuId(null); setActiveMenuProfile(null); }}
          onMenuUpdate={(updated) => {
            setActiveMenu(updated);
            // Also refresh the menu list item
            setItems((arr) => (arr || []).map((m) => m.menu_id === activeMenuId ? { ...m, menu: updated } : m));
          }}
        />
      )}
    </div>
  );
}
